import { useEffect, useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_PAYMENT_API;

const MotorRenewalMigration = ({ toast }) => {
  const currentDate = new Date();

  // Tab state
  const [activeTab, setActiveTab] = useState('status'); // 'status' or 'download'

  // BNI Status Report state
  const [bniYear, setBniYear] = useState(currentDate.getFullYear());
  const [bniData, setBniData] = useState(null);
  const [bniLoading, setBniLoading] = useState(false);
  const [bniError, setBniError] = useState(false);

  // Download Report state
  const [month, setMonth] = useState(currentDate.getMonth() + 1);
  const [year, setYear] = useState(currentDate.getFullYear());
  const [downloadLoading, setDownloadLoading] = useState(false);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [];
  for (let y = currentDate.getFullYear(); y >= 2025; y--) {
    years.push(y);
  }

  // Fetch BNI Status Report
  const fetchBniStatus = async () => {
    setBniLoading(true);
    setBniError(false);

    try {
      const url = `${API_BASE_URL}reports/BniStatusByYear?year=${bniYear}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load BNI status');
      }

      const data = await response.json();
      setBniData(data);
    } catch (err) {
      console.error('BNI Status error:', err);
      setBniError(true);
      toast?.error('Failed to load BNI status report.', {
        style: { background: '#ffcccc', color: 'black', fontWeight: 'bold' },
      });
    } finally {
      setBniLoading(false);
    }
  };

  useEffect(() => {
    fetchBniStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bniYear]);

  const handleBniRefresh = () => {
    fetchBniStatus();
  };

  // Download Failed/Done Report
  const downloadReport = async () => {
    setDownloadLoading(true);

    try {
      const url = `${API_BASE_URL}reports/DownloadFailedDoneByMonth?month=${month}&year=${year}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      if (!response.ok) {
        let errorMessage = 'Failed to download report.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // Response is not JSON
        }
        toast?.error(errorMessage, {
          style: { background: '#ffcccc', color: 'black', fontWeight: 'bold' },
        });
        setDownloadLoading(false);
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `MotorRenewalMigration_${months.find(m => m.value === month)?.label}_${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast?.success('Report downloaded successfully!', {
        style: { background: '#4ade80', color: 'white' },
      });
    } catch (err) {
      console.error('Download error:', err);
      toast?.error('Failed to download report.', {
        style: { background: '#ffcccc', color: 'black', fontWeight: 'bold' },
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  return (
    <div className="p-6">
      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-4">
          <button
            onClick={() => setActiveTab('status')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'status'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Migration Status
          </button>
          <button
            onClick={() => setActiveTab('download')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'download'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Download Report
          </button>
        </nav>
      </div>

      {/* BNI Status Report Section */}
      {activeTab === 'status' && (
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">BNI Migration Status</h2>
              <p className="mt-1 text-sm text-gray-500">
                Policy and Party migration status by year
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={bniYear}
                onChange={(e) => setBniYear(Number(e.target.value))}
                disabled={bniLoading}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <button
                onClick={handleBniRefresh}
                disabled={bniLoading}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white shadow-sm hover:bg-gray-50 disabled:opacity-60"
              >
                Refresh
              </button>
            </div>
          </div>

          {bniLoading ? (
            <div className="flex items-center justify-center p-10">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
            </div>
          ) : bniError ? (
            <div className="rounded-xl border border-dashed border-red-300 bg-white p-6 text-sm text-red-700">
              Couldn't load the BNI status report. Please try again.
            </div>
          ) : bniData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Policy Summary */}
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Policy Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Success</span>
                      <span className="text-sm font-semibold text-green-600">{formatNumber(bniData.totalPolicySuccess)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Failed</span>
                      <span className="text-sm font-semibold text-red-600">{formatNumber(bniData.totalPolicyFailed)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Pending</span>
                      <span className="text-sm font-semibold text-blue-600">{formatNumber(bniData.totalPolicyInserted)}</span>
                    </div>
                  </div>
                </div>

                {/* Party Summary */}
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Party Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Success</span>
                      <span className="text-sm font-semibold text-green-600">{formatNumber(bniData.totalPartySuccess)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Failed</span>
                      <span className="text-sm font-semibold text-red-600">{formatNumber(bniData.totalPartyFailed)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Pending</span>
                      <span className="text-sm font-semibold text-blue-600">{formatNumber(bniData.totalPartyInserted)}</span>
                    </div>
                  </div>
                </div>

                {/* Generated Info */}
                <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Report Info</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Year</span>
                      <span className="text-sm font-semibold text-gray-900">{bniData.year}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Generated</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {new Date(bniData.generatedAt).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monthly Breakdown Table */}
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Month
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-green-600">
                        Policy Success
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-red-600">
                        Policy Failed
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-blue-600">
                        Policy Pending
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-green-600">
                        Party Success
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-red-600">
                        Party Failed
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-blue-600">
                        Party Pending
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {bniData.months?.map((m) => (
                      <tr key={m.month} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                          {m.monthName}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-green-600">
                          {formatNumber(m.policySuccess)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-red-600">
                          {formatNumber(m.policyFailed)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-blue-600">
                          {formatNumber(m.policyInserted)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-green-600">
                          {formatNumber(m.partySuccess)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-red-600">
                          {formatNumber(m.partyFailed)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-blue-600">
                          {formatNumber(m.partyInserted)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr className="font-semibold">
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                        Total
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-green-600">
                        {formatNumber(bniData.totalPolicySuccess)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-red-600">
                        {formatNumber(bniData.totalPolicyFailed)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-blue-600">
                        {formatNumber(bniData.totalPolicyInserted)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-green-600">
                        {formatNumber(bniData.totalPartySuccess)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-red-600">
                        {formatNumber(bniData.totalPartyFailed)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-blue-600">
                        {formatNumber(bniData.totalPartyInserted)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Download Report Section */}
      {activeTab === 'download' && (
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Download Migration Report</h2>
            <p className="mt-1 text-sm text-gray-500">
              Download failed/done motor renewal migration report by month and year
            </p>
          </div>

          <div className="max-w-xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-6 flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px]">
                <label htmlFor="download-month" className="block text-sm font-medium text-gray-700 mb-1">
                  Month
                </label>
                <select
                  id="download-month"
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  disabled={downloadLoading}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                >
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[120px]">
                <label htmlFor="download-year" className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <select
                  id="download-year"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  disabled={downloadLoading}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-60"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={downloadReport}
              disabled={downloadLoading}
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {downloadLoading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download Excel Report
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotorRenewalMigration;
